import { toUserMessage } from '../../lib/errors.js';
// AuditPage — Compliance HACCP / ispezione ASL.
// Mostra: stato hash-chain (ultimo anchor RFC3161, root Merkle, età, eventi totali),
// pulsante "Verifica catena" (RPC verify_chain_integrity), elenco anchor storici,
// log modifiche anagrafiche (audit_log) ultimi 30gg. Esportazione CSV.

import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { checkmarkCircle, warningOutline, shieldCheckmarkOutline, downloadOutline, refreshOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';

interface AuditStatus {
  tenant_id: string;
  last_anchor: {
    id: string;
    anchored_at: string;
    period_start: string;
    period_end: string;
    events_count: number;
    root_hash: string;
    tsa_provider: string;
  } | null;
  anchors_count: number;
  events_count: number;
  last_event_ts: string | null;
  audit_log_count_30d: number;
}

interface AnchorRow {
  id: string;
  anchored_at: string;
  period_start: string;
  period_end: string;
  events_count: number;
  root_hash: string;
  tsa_provider: string;
}

interface AuditLogRow {
  id: number;
  table_name: string;
  row_id: string;
  op: 'INSERT' | 'UPDATE' | 'DELETE';
  by_user: string | null;
  at: string;
  old_row: Record<string, unknown> | null;
  new_row: Record<string, unknown> | null;
}

interface VerifyResult {
  events_total: number;
  events_checked: number;
  scope_complete: boolean;
  status: 'verified' | 'legacy_partial' | 'scope_partial' | 'scope_changed' | 'broken';
  linkage_breaks: number;
  rev_gaps: number;
  hash_mismatches: number;
  forks_v2: number;
  hashes_verified: number;
  verified_from_rev: number | null;
  legacy_events: number;
  legacy_business_scope_violations: number;
  broken_count: number;
  broken_revs: number[];
  is_intact: boolean;
  cryptographically_verified: boolean;
  checked_at: string;
}

function fmtDateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

function ageInDays(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.round((Date.now() - Date.parse(iso)) / 86_400_000);
}

function shortHash(hex: string | null | undefined): string {
  if (!hex) return '—';
  return `${hex.slice(0, 8)}…${hex.slice(-6)}`;
}

export function AuditPage(): JSX.Element {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const [tab, setTab] = useState<'chain' | 'anchors' | 'audit'>('chain');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data: status, isLoading: loadingStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['audit_status', tenantId],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await getSupabase().rpc('get_audit_status', { p_tenant_id: tenantId });
      if (error) throw error;
      return data as AuditStatus;
    },
  });

  const { data: anchors = [], isLoading: loadingAnchors } = useQuery({
    queryKey: ['event_anchors', tenantId],
    enabled: !!tenantId && tab === 'anchors',
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('event_anchors')
        .select('id,anchored_at,period_start,period_end,events_count,root_hash,tsa_provider')
        .eq('tenant_id', tenantId)
        .order('anchored_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as AnchorRow[];
    },
  });

  const { data: auditRows = [], isLoading: loadingAudit } = useQuery({
    queryKey: ['audit_log', tenantId],
    enabled: !!tenantId && tab === 'audit',
    staleTime: 30_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString();
      const { data, error } = await getSupabase()
        .from('audit_log')
        .select('id,table_name,row_id,op,by_user,at,old_row,new_row')
        .eq('tenant_id', tenantId)
        .gte('at', since)
        .order('at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as AuditLogRow[];
    },
  });

  async function verify(): Promise<void> {
    if (!tenantId) return;
    setVerifying(true);
    try {
      const { data, error } = await getSupabase().rpc('verify_chain_integrity', {
        p_tenant_id: tenantId,
        p_limit: 0,
      });
      if (error) throw error;
      const result = data as VerifyResult;
      setVerifyResult(result);
      if (result.status === 'scope_changed') {
        setToast('Sono arrivati nuovi eventi durante il controllo: riprova');
      } else if (result.status === 'broken') {
        setToast(`⚠️ Trovati ${result.broken_count} problemi nella catena`);
      } else if (result.cryptographically_verified) {
        setToast(`Catena V2 verificata · ${result.hashes_verified} eventi`);
      } else {
        setToast(
          `Struttura integra · ${result.hashes_verified} hash V2 verificati · `
          + `${result.legacy_events} eventi storici legacy`,
        );
      }
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setVerifying(false);
    }
  }

  function exportAnchorsCsv(): void {
    if (!anchors.length) return;
    const header = 'id,anchored_at,period_start,period_end,events_count,root_hash,tsa_provider';
    const rows = anchors.map((a) =>
      [a.id, a.anchored_at, a.period_start, a.period_end, a.events_count, a.root_hash, a.tsa_provider]
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `niveo-anchors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportAuditCsv(): void {
    if (!auditRows.length) return;
    const header = 'id,at,table_name,row_id,op,by_user';
    const rows = auditRows.map((r) =>
      [r.id, r.at, r.table_name, r.row_id, r.op, r.by_user ?? '']
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `niveo-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const lastAnchorAge = ageInDays(status?.last_anchor?.anchored_at);
  const anchorWarn = lastAnchorAge !== null && lastAnchorAge > 14;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/settings" /></IonButtons>
          <IonTitle>Audit & Compliance</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void refetchStatus()} aria-label="Aggiorna">
              <IonIcon slot="icon-only" icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={tab} onIonChange={(ev) => setTab(ev.detail.value as typeof tab)}>
            <IonSegmentButton value="chain"><IonLabel>Catena</IonLabel></IonSegmentButton>
            <IonSegmentButton value="anchors"><IonLabel>Anchor</IonLabel></IonSegmentButton>
            <IonSegmentButton value="audit"><IonLabel>Audit log</IonLabel></IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding" style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        {/* TAB: catena */}
        {tab === 'chain' && (
          <>
            {loadingStatus ? (
              <IonSpinner name="dots" />
            ) : status ? (
              <>
                <IonList inset>
                  <IonItem lines="none">
                    <IonIcon icon={shieldCheckmarkOutline} slot="start" color="primary" />
                    <IonLabel>
                      <h2 style={{ fontWeight: 700 }}>Stato hash-chain</h2>
                      <p>
                        {status.events_count} eventi totali ·{' '}
                        ultimo: {fmtDateTime(status.last_event_ts)}
                      </p>
                    </IonLabel>
                  </IonItem>
                  <IonItem>
                    <IonLabel>Ultimo anchor RFC3161</IonLabel>
                    <IonNote slot="end" color={anchorWarn ? 'warning' : 'medium'}>
                      {status.last_anchor ? `${fmtDateTime(status.last_anchor.anchored_at)}` : 'mai'}
                      {lastAnchorAge !== null && ` (${lastAnchorAge}gg fa)`}
                    </IonNote>
                  </IonItem>
                  {anchorWarn && (
                    <IonItem color="warning">
                      <IonIcon icon={warningOutline} slot="start" />
                      <IonLabel className="ion-text-wrap">
                        L'ultimo anchor risale a {lastAnchorAge} giorni fa. Il cron settimanale
                        potrebbe non essere attivo. Verifica la configurazione GUC <code>niveo.hash_anchor_cron_secret</code>.
                      </IonLabel>
                    </IonItem>
                  )}
                  {status.last_anchor && (
                    <>
                      <IonItem>
                        <IonLabel>Periodo coperto</IonLabel>
                        <IonNote slot="end">
                          {new Date(status.last_anchor.period_start).toLocaleDateString('it-IT')} →{' '}
                          {new Date(status.last_anchor.period_end).toLocaleDateString('it-IT')}
                        </IonNote>
                      </IonItem>
                      <IonItem>
                        <IonLabel>Eventi nell'anchor</IonLabel>
                        <IonNote slot="end">{status.last_anchor.events_count}</IonNote>
                      </IonItem>
                      <IonItem>
                        <IonLabel>Root hash (Merkle)</IonLabel>
                        <IonNote slot="end" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {shortHash(status.last_anchor.root_hash)}
                        </IonNote>
                      </IonItem>
                      <IonItem>
                        <IonLabel>Provider TSA</IonLabel>
                        <IonNote slot="end">{status.last_anchor.tsa_provider}</IonNote>
                      </IonItem>
                    </>
                  )}
                  <IonItem>
                    <IonLabel>Anchor totali</IonLabel>
                    <IonNote slot="end">{status.anchors_count}</IonNote>
                  </IonItem>
                  <IonItem>
                    <IonLabel>Modifiche anagrafiche (30gg)</IonLabel>
                    <IonNote slot="end">{status.audit_log_count_30d}</IonNote>
                  </IonItem>
                </IonList>

                <IonList inset>
                  <IonListHeader><IonLabel>Verifica integrità</IonLabel></IonListHeader>
                  <IonItem lines="none">
                    <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
                      Controlla linkage e revisioni di tutto lo storico, poi ricalcola il contenuto
                      degli eventi con hash V2. Gli eventi precedenti restano dichiarati legacy:
                      sono collegati, ma non vengono presentati come prova crittografica completa.
                    </IonLabel>
                  </IonItem>
                  <div style={{ padding: '0 16px 16px' }}>
                    <IonButton expand="block" disabled={verifying} onClick={() => void verify()}>
                      {verifying ? <IonSpinner name="dots" /> : 'Verifica catena'}
                    </IonButton>
                  </div>
                  {verifyResult && (
                    <IonItem
                      color={verifyResult.status === 'broken'
                        ? 'danger'
                        : verifyResult.cryptographically_verified
                          ? 'success'
                          : 'warning'}
                    >
                      <IonIcon
                        icon={verifyResult.status === 'broken'
                          || verifyResult.status === 'scope_changed'
                          ? warningOutline
                          : checkmarkCircle}
                        slot="start"
                      />
                      <IonLabel className="ion-text-wrap">
                        <h3>
                          {verifyResult.status === 'scope_changed'
                            ? 'Sono arrivati nuovi eventi: ripeti la verifica'
                            : verifyResult.status === 'broken'
                              ? `${verifyResult.broken_count} problemi su ${verifyResult.events_checked} eventi`
                              : verifyResult.cryptographically_verified
                                ? `Catena V2 VERIFICATA — ${verifyResult.hashes_verified} eventi`
                                : `Struttura integra — storico legacy: ${verifyResult.legacy_events} eventi`}
                        </h3>
                        <p style={{ fontSize: 12 }}>
                          Verifica del {fmtDateTime(verifyResult.checked_at)}
                        </p>
                        {verifyResult.hashes_verified > 0 && !verifyResult.cryptographically_verified && (
                          <p style={{ fontSize: 12 }}>
                            Hash V2 ricalcolati: {verifyResult.hashes_verified}
                            {verifyResult.verified_from_rev !== null
                              && ` · dalla rev ${verifyResult.verified_from_rev}`}
                          </p>
                        )}
                        {verifyResult.status === 'broken' && verifyResult.broken_revs.length > 0 && (
                          <p style={{ fontSize: 12 }}>
                            Revisioni da controllare: {verifyResult.broken_revs.join(', ')}
                          </p>
                        )}
                        {verifyResult.legacy_business_scope_violations > 0 && (
                          <p style={{ fontSize: 12 }}>
                            Anomalie legacy sede/tenant da revisionare:{' '}
                            {verifyResult.legacy_business_scope_violations}
                          </p>
                        )}
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              </>
            ) : (
              <IonText color="medium">Nessun dato disponibile.</IonText>
            )}
          </>
        )}

        {/* TAB: anchor */}
        {tab === 'anchors' && (
          <>
            <IonList inset>
              <IonItem lines="none">
                <IonLabel>
                  <h3>Anchor RFC3161 ({anchors.length})</h3>
                  <IonNote style={{ fontSize: 11 }}>
                    Cron settimanale via edge function <code>hash-anchor</code>. Ogni anchor
                    timestampa la radice Merkle degli eventi della settimana presso una TSA pubblica.
                  </IonNote>
                </IonLabel>
                {anchors.length > 0 && (
                  <IonButton slot="end" fill="outline" size="small" onClick={exportAnchorsCsv}>
                    <IonIcon icon={downloadOutline} slot="start" />
                    CSV
                  </IonButton>
                )}
              </IonItem>
            </IonList>
            {loadingAnchors ? (
              <IonSpinner name="dots" />
            ) : anchors.length === 0 ? (
              <IonItem><IonLabel color="medium">Nessun anchor ancora generato.</IonLabel></IonItem>
            ) : (
              <IonList inset>
                {anchors.map((a) => (
                  <IonItem key={a.id}>
                    <IonLabel className="ion-text-wrap">
                      <h3>{fmtDateTime(a.anchored_at)}</h3>
                      <p style={{ fontSize: 12 }}>
                        {new Date(a.period_start).toLocaleDateString('it-IT')} →{' '}
                        {new Date(a.period_end).toLocaleDateString('it-IT')} · {a.events_count} eventi
                      </p>
                      <p style={{ fontSize: 11, fontFamily: 'monospace', color: '#0066cc' }}>
                        {shortHash(a.root_hash)}
                      </p>
                    </IonLabel>
                    <IonBadge slot="end" color="medium">{a.tsa_provider}</IonBadge>
                  </IonItem>
                ))}
              </IonList>
            )}
          </>
        )}

        {/* TAB: audit log */}
        {tab === 'audit' && (
          <>
            <IonList inset>
              <IonItem lines="none">
                <IonLabel>
                  <h3>Modifiche anagrafiche ({auditRows.length}/200, ultimi 30gg)</h3>
                  <IonNote style={{ fontSize: 11 }}>
                    Trigger automatico su tabelle: foods, suppliers, recipes, businesses, printers,
                    storage_units, probes, cleaning_*, label_templates, fiscal/AI config.
                  </IonNote>
                </IonLabel>
                {auditRows.length > 0 && (
                  <IonButton slot="end" fill="outline" size="small" onClick={exportAuditCsv}>
                    <IonIcon icon={downloadOutline} slot="start" />
                    CSV
                  </IonButton>
                )}
              </IonItem>
            </IonList>
            {loadingAudit ? (
              <IonSpinner name="dots" />
            ) : auditRows.length === 0 ? (
              <IonItem><IonLabel color="medium">Nessuna modifica registrata negli ultimi 30 giorni.</IonLabel></IonItem>
            ) : (
              <IonList inset>
                {auditRows.map((r) => {
                  const opColor = r.op === 'DELETE' ? 'danger' : r.op === 'INSERT' ? 'success' : 'warning';
                  const summary =
                    (r.new_row && (r.new_row.name as string)) ??
                    (r.old_row && (r.old_row.name as string)) ??
                    r.row_id.slice(0, 8);
                  return (
                    <IonItem key={r.id}>
                      <IonBadge slot="start" color={opColor}>{r.op}</IonBadge>
                      <IonLabel className="ion-text-wrap">
                        <h3>{r.table_name} · {summary}</h3>
                        <p style={{ fontSize: 12 }}>{fmtDateTime(r.at)}</p>
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
          </>
        )}

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={3000}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
